import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import Api, { paths } from 'shared/services/Api'
import {
  PaymentMethod,
  LoadingStatus,
  RejectValue,
  AnyProduct,
  ApiCreateOrder,
  SummaryFormValues,
  AdministrativePayment,
} from 'shared/types'
import type { RootState } from 'app'
import {
  getAdministrativePaymentProductState,
  getPaymentMethodsState,
} from './selectors'
import {
  ApiSubscription,
  ApiAdditionalPackage,
  ApiCart,
  CartDetails,
  CreateOrderPayload,
} from './actions.types'

const NAMESPACE = 'payments'

export const setSummaryFormValues = createAction<SummaryFormValues | null>(
  `${NAMESPACE}/setSummaryFormValues`
)

export const createOrder = createAsyncThunk<
  ApiCreateOrder,
  CreateOrderPayload,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/createOrder`, async (data, thunkAPI) => {
  try {
    const response = await Api.post<ApiCreateOrder>(paths.Payments.orders, data)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const selectProduct = createAsyncThunk<
  ApiCart,
  AnyProduct | AdministrativePayment,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/selectProduct`, async (data, thunkAPI) => {
  try {
    const response = await Api.post<ApiCart>(paths.Payments.carts, {
      productIdentifier: `${data.identifier}`,
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getAvailableSubscriptions = createAsyncThunk<
  ApiSubscription[],
  undefined,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/getAvailableSubscriptions`, async (_, thunkAPI) => {
  try {
    const response = await Api.get<ApiSubscription[]>(
      paths.Products.subscriptions
    )
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getAdministrativePaymentProduct = createAsyncThunk<
  AdministrativePayment,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: { error: string; errorCode: string } }
>(
  `${NAMESPACE}/getAdministrativePaymentProduct`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<AdministrativePayment>(
        paths.Products.administrativePayment
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getAdministrativePaymentProductState(
        getState() as RootState
      )
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const getAdditionalPackages = createAsyncThunk<
  ApiAdditionalPackage[],
  undefined,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/getAdditionalPackages`, async (_, thunkAPI) => {
  try {
    const response = await Api.get<ApiAdditionalPackage[]>(
      paths.Products.additionalPackages
    )
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const applyPromoCode = createAsyncThunk<
  CartDetails,
  { couponName: string; cartId: number },
  { rejectValue: { error: string; errorCode: string } }
>(
  `${NAMESPACE}/applyPromoCode`,
  async ({ couponName, cartId }, { rejectWithValue }) => {
    try {
      const response = await Api.put(
        `${paths.Payments.carts}/${cartId}/coupon`,
        {
          couponName,
          cartId,
        }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const removePromoCode = createAsyncThunk<
  CartDetails,
  string | number,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/removePromoCode`, async (cartId, { rejectWithValue }) => {
  try {
    const response = await Api.delete<CartDetails>(
      `${paths.Payments.carts}/${cartId}/coupon`
    )
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getPaymentMethods = createAsyncThunk<
  PaymentMethod[],
  { disableRefetch?: boolean } | undefined,
  { rejectValue: RejectValue }
>(
  `${NAMESPACE}/getPaymentMethods`,
  async (_, thunkAPI) => {
    try {
      const response = await Api.get<PaymentMethod[]>(
        paths.Payments.paymentMethods
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error as RejectValue)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getPaymentMethodsState(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)
